import { useEffect, useState } from "react";
import { ExpandedContent, Separator, TableContainer, AnexosContainer, ArquivoItem } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import { CampoAlterado, ArquivoConclusao } from "./types";
import { mapearCamposAlterados, mapearArquivosConclusao } from "./utils";
import { FileText, ExternalLink } from "lucide-react";
import LogsVazio from "../../Compartilhado/LogsVazio";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

export default function ExpandedContentConclusaoProfissional({ 
    item, 
    onNavigateToFile 
}: ExpandedContentProps) {
    const [camposAlterados, setCamposAlterados] = useState<CampoAlterado[]>([]);
    const [arquivos, setArquivos] = useState<ArquivoConclusao[]>([]);
    const [observacoes, setObservacoes] = useState<string | null>(null);
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        if (!item.id) {
            setCarregando(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setCarregando(true);
                const resposta = await api.get(`log-historico/${item.id}`);

                if (resposta.data.status === 'success') {
                    const logs = resposta.data.result || [];
                    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');
                    const logsArquivos = logs.filter((log: any) => log.entidade_nome === 'proposta_arquivo');

                    setCamposAlterados(mapearCamposAlterados(logProposta));
                    setArquivos(mapearArquivosConclusao(logsArquivos));
                } else {
                    setCamposAlterados([]);
                    setArquivos([]);
                }

                if (item.justificativa) {
                    setObservacoes(item.justificativa);
                }
            } catch (erro) {
                setCamposAlterados([]);
                setArquivos([]);
                setObservacoes(null);
            } finally {
                setCarregando(false);
            }
        };

        buscarDados();
    }, [item.id, item.justificativa]);

    const handleArquivoClick = (arquivo: ArquivoConclusao) => {
        if (onNavigateToFile && arquivo.id_proposta_arquivo) {
            onNavigateToFile(arquivo.id_proposta_arquivo);
        }
    };

    if (carregando) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const semDados = camposAlterados.length === 0 && arquivos.length === 0 && !observacoes;

    return (
        <ExpandedContent>
            <Separator />

            {camposAlterados.length > 0 && (
                <div className="expanded-section">
                    <h4 className="section-title">Principal</h4>
                    <TableContainer>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="field-col">Campo</th>
                                    <th className="before-col">Antes</th>
                                    <th className="after-col">Depois</th>
                                </tr>
                            </thead>
                            <tbody>
                                {camposAlterados.map((campo, index) => (
                                    <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                        <td className="field-cell">{campo.label}</td>
                                        <td className="before-cell">{campo.antes}</td>
                                        <td className="after-cell">
                                            <div className="after-content">
                                                <span className="highlight-bar" />
                                                <span>{campo.depois}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableContainer>
                </div>
            )}

            {observacoes && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Observações</h4>
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                            borderRadius: '6px',
                            color: 'black',
                            fontSize: '11px',
                            lineHeight: '20px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {observacoes}
                        </div>
                    </div>
                </>
            )}

            {arquivos.length > 0 && (
                <div className="expanded-section">
                    <h4 className="section-title">Anexos</h4>
                    <AnexosContainer>
                        {arquivos.map((arquivo) => (
                            <ArquivoItem 
                                key={arquivo.id_proposta_arquivo} 
                                onClick={() => handleArquivoClick(arquivo)}
                            >
                                <div className="arquivo-icone">
                                    <FileText size={16} color="#64748B" />
                                </div>
                                <div className="arquivo-info">
                                    <div className="arquivo-nome">
                                        {arquivo.caminho.split('/').pop() || arquivo.caminho}
                                    </div>
                                    <div className="arquivo-tamanho">
                                        {arquivo.tamanho}
                                    </div>
                                </div>
                                <div className="arquivo-acao">
                                    <ExternalLink size={14} color="#64748B" />
                                </div>
                            </ArquivoItem>
                        ))}
                    </AnexosContainer>
                </div>
            )}

            {semDados && (
                <>
                    <Separator />
                    <LogsVazio />
                </>
            )}
        </ExpandedContent>
    );
}

